import React, { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import PaginationFooter from "../../../components/common/PaginationFooter";

function formatDecimal(value, digits = 3) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toFixed(digits).replace(/\.?0+$/, "");
}

function formatMoney(value) {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(2)} PLN` : String(value);
}

function productUnit(product) {
  return product?.stockUnit?.symbol || product?.stock_unit_code || "";
}

function StockProductBrowser({
  products = [],
  selectedProductId = "",
  query = "",
  onQueryChange,
  locationId = "",
  onLocationChange,
  locations = [],
  onlyWithStock = false,
  onOnlyWithStockChange,
  loading = false,
  page = 1,
  pageCount = 1,
  pageSize = 25,
  pageSizeOptions = [25, 50, 100],
  total = 0,
  onPageSizeChange,
  onPrev,
  onNext,
  onSelect,
}) {
  const { t } = useTranslation(["common", "stock"]);
  const rowRefs = useRef([]);

  const selectedLocation = useMemo(
    () => locations.find((location) => String(location.id) === String(locationId)) || null,
    [locations, locationId]
  );

  function focusAndSelect(index) {
    if (!products.length) return;
    const safeIndex = Math.min(Math.max(index, 0), products.length - 1);
    const product = products[safeIndex];
    onSelect?.(product);
    window.requestAnimationFrame(() => rowRefs.current[safeIndex]?.focus());
  }

  function handleKeyDown(event, index) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusAndSelect(index + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusAndSelect(index - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusAndSelect(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusAndSelect(products.length - 1);
    }
  }

  return (
    <aside className="stock-product-browser" aria-label={t("stock:workspace.browserAriaLabel")}>
      <div className="stock-product-browser__header">
        <div>
          <span className="stock-product-browser__eyebrow">{t("stock:workspace.masterEyebrow")}</span>
          <h2>{t("stock:workspace.productsTitle")}</h2>
        </div>
        <span className="stock-product-browser__total">{t("stock:workspace.productsCount", { count: total })}</span>
      </div>

      <div className="stock-product-browser__filters">
        <label>
          <span>{t("common:actions.search")}</span>
          <input
            type="search"
            value={query}
            onChange={(event) => onQueryChange?.(event.target.value)}
            placeholder={t("stock:workspace.productSearchPlaceholder")}
            autoComplete="off"
          />
        </label>

        <label>
          <span>{t("stock:overview.location")}</span>
          <select value={locationId} onChange={(event) => onLocationChange?.(event.target.value)}>
            <option value="">{t("stock:overview.allLocations")}</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>{location.name}</option>
            ))}
          </select>
        </label>

        <label className="stock-product-browser__check">
          <input
            type="checkbox"
            checked={onlyWithStock}
            onChange={(event) => onOnlyWithStockChange?.(event.target.checked)}
          />
          <span>{t("stock:workspace.onlyWithStock")}</span>
        </label>
      </div>

      {selectedLocation ? (
        <div className="stock-product-browser__filter-note">
          {t("stock:workspace.locationFilterNote", { location: selectedLocation.name })}
        </div>
      ) : null}

      <div className="stock-product-browser__list" role="listbox" aria-busy={loading}>
        {!loading && products.length === 0 ? (
          <div className="stock-product-browser__empty">
            <strong>{t("stock:workspace.noProductsTitle")}</strong>
            <span>{t("stock:workspace.noProductsHint")}</span>
          </div>
        ) : null}

        {loading && products.length === 0 ? (
          <div className="stock-product-browser__empty">{t("common:common.loading")}</div>
        ) : null}

        {products.map((product, index) => {
          const selected = String(product.id) === String(selectedProductId);
          const unit = productUnit(product) || "pcs";
          const selectedLocationQuantity = product.selected_location_quantity;

          return (
            <button
              key={product.id}
              ref={(node) => { rowRefs.current[index] = node; }}
              type="button"
              role="option"
              aria-selected={selected}
              className={selected ? "stock-product-browser__row active" : "stock-product-browser__row"}
              onClick={() => onSelect?.(product)}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              <span className="stock-product-browser__row-main">
                <strong>{product.name_public || `#${product.id}`}</strong>
                <small>{product.variant_label || product.sku || product.barcode || ""}</small>
              </span>

              <span className="stock-product-browser__row-stock">
                <strong>{formatDecimal(product.global_stock_quantity, 3)} {unit}</strong>
                <small>{formatMoney(product.global_stock_value_net)}</small>
                {selectedLocation && selectedLocationQuantity !== null && selectedLocationQuantity !== undefined ? (
                  <em>{selectedLocation.name}: {formatDecimal(selectedLocationQuantity, 3)} {unit}</em>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>

      <PaginationFooter
        className="stock-product-browser__pagination"
        page={page}
        pageCount={pageCount}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        totalRows={total}
        disabled={loading}
        onPageSizeChange={onPageSizeChange}
        onPrev={onPrev}
        onNext={onNext}
      />
    </aside>
  );
}

export default StockProductBrowser;
